import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import HeroSection from "../../components/HeroSection/HeroSection.jsx";
import ProjectCard from "../../components/ProjectCard/ProjectCard.jsx";
import {
    MainContainer,
} from "../../utils/style/elements.tsx";
import colors from "../../utils/style/colors.tsx";
import projectData from "../../datas/projectData";
import { useScrollReveal } from "../../utils/hooks/useScrollReveal";

// Animation de fade in + slide up
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(50px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// Wrapper animé
const AnimatedSection = styled.div`
    opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
    animation: ${({ $isVisible }) => ($isVisible ? fadeInUp : "none")} 0.8s ease-out;
    animation-delay: ${({ $delay }) => $delay || "0s"};
    animation-fill-mode: both;
`;

const FilterSection = styled.div`
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-bottom: 50px;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 15px;
        align-items: center;
    }
`;

const FilterButton = styled.button`
    padding: 12px 30px;
    background-color: ${({ $active }) => ($active ? colors.dark_red : colors.white)};
    color: ${({ $active }) => ($active ? colors.white : colors.black)};
    border: solid 3px ${colors.black};
    box-shadow: ${({ $active }) => ($active ? "5px 5px" : "4px 4px")} ${colors.black};
    border-radius: 20px;
    font-size: 18px;
    font-family: "Archivo Black", sans-serif;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: translate(-3px, -3px);
        box-shadow: 7px 7px ${colors.black};
        background-color: ${({ $active }) => ($active ? colors.dark_red : colors.light_pink)};
    }

    @media (max-width: 768px) {
        width: 80%;
        font-size: 16px;
        padding: 10px 20px;
    }
`;

const ProjectsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 40px;
    margin-bottom: 5vw;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
`;

const CategorySection = styled.div`
    margin-bottom: 5vw;
`;

const CategoryTitle = styled.h2`
    font-family: "Archivo Black", sans-serif;
    font-size: 36px;
    color: ${colors.h1_red};
    margin-bottom: 30px;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 24px;
        margin-bottom: 20px;
    }
`;

const StatsSection = styled.div`
    display: flex;
    justify-content: space-around;
    margin-bottom: 50px;
    padding: 30px;
    background-color: ${colors.light_pink};
    border-radius: 20px;
    border: solid 5px ${colors.black};

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 20px;
        padding: 20px;
    }
`;

const StatItem = styled.div`
    text-align: center;
`;

const StatNumber = styled.div`
    font-family: "Archivo Black", sans-serif;
    font-size: 48px;
    color: ${colors.dark_red};

    @media (max-width: 768px) {
        font-size: 36px;
    }
`;

const StatLabel = styled.div`
    font-family: "Inter", sans-serif;
    font-size: 18px;
    color: ${colors.black};

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

function Projets() {
    const [activeFilter, setActiveFilter] = useState("tous");
    const [refStats, isVisibleStats] = useScrollReveal({ once: true, threshold: 0.2 });
    const [refFilters, isVisibleFilters] = useScrollReveal({ once: true, threshold: 0.2 });
    const [refWeb, isVisibleWeb] = useScrollReveal({ once: true, threshold: 0.1 });
    const [refDesign, isVisibleDesign] = useScrollReveal({ once: true, threshold: 0.1 });

    const webProjects = projectData.filter((p) => p.category === "web");
    const designProjects = projectData.filter((p) => p.category === "design");

    const filteredProjects = 
        activeFilter === "tous" 
            ? projectData 
            : projectData.filter((p) => p.category === activeFilter);

    return (
        <React.StrictMode>
            <HeroSection page="projets" />
            <MainContainer>
                {/* Section statistiques */}
                <AnimatedSection ref={refStats} $isVisible={isVisibleStats}>
                    <StatsSection>
                        <StatItem>
                            <StatNumber>{projectData.length}</StatNumber>
                            <StatLabel>Projets réalisés</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>{webProjects.length}</StatNumber>
                            <StatLabel>Sites web</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatNumber>{designProjects.length}</StatNumber>
                            <StatLabel>Designs graphiques</StatLabel>
                        </StatItem>
                    </StatsSection>
                </AnimatedSection>

                {/* Filtres */}
                <AnimatedSection ref={refFilters} $isVisible={isVisibleFilters}>
                    <FilterSection>
                        <FilterButton
                            $active={activeFilter === "tous"}
                            onClick={() => setActiveFilter("tous")}
                        >
                            Tous les projets
                        </FilterButton>
                        <FilterButton
                            $active={activeFilter === "web"}
                            onClick={() => setActiveFilter("web")}
                        >
                            Sites web
                        </FilterButton>
                        <FilterButton
                            $active={activeFilter === "design"}
                            onClick={() => setActiveFilter("design")}
                        >
                            Design graphique
                        </FilterButton>
                    </FilterSection>
                </AnimatedSection>

                {/* Affichage selon le filtre */}
                {activeFilter === "tous" ? (
                    <>
                        {/* Section Sites Web */}
                        <AnimatedSection ref={refWeb} $isVisible={isVisibleWeb}>
                            <CategorySection>
                                <CategoryTitle>Création de sites web</CategoryTitle>
                                <ProjectsGrid>
                                    {webProjects.map((project, index) => (
                                        <AnimatedSection
                                            key={project.index}
                                            $isVisible={isVisibleWeb}
                                            $delay={`${index * 0.1}s`}
                                        >
                                            <ProjectCard project={project} />
                                        </AnimatedSection>
                                    ))}
                                </ProjectsGrid>
                            </CategorySection>
                        </AnimatedSection>

                        {/* Section Design */}
                        <AnimatedSection ref={refDesign} $isVisible={isVisibleDesign}>
                            <CategorySection>
                                <CategoryTitle>Mes projets de design</CategoryTitle>
                                <ProjectsGrid>
                                    {designProjects.map((project, index) => (
                                        <AnimatedSection
                                            key={project.index}
                                            $isVisible={isVisibleDesign}
                                            $delay={`${index * 0.1}s`}
                                        >
                                            <ProjectCard project={project} />
                                        </AnimatedSection>
                                    ))}
                                </ProjectsGrid>
                            </CategorySection>
                        </AnimatedSection>
                    </>
                ) : (
                    <CategorySection>
                        <ProjectsGrid>
                            {filteredProjects.map((project, index) => (
                                <AnimatedSection
                                    key={project.index}
                                    $isVisible={isVisibleFilters}
                                    $delay={`${index * 0.1}s`}
                                >
                                    <ProjectCard project={project} />
                                </AnimatedSection>
                            ))}
                        </ProjectsGrid>
                    </CategorySection>
                )}
            </MainContainer>
        </React.StrictMode>
    );
}

export default Projets;
